import { useLayoutEffect, useState, type RefObject } from 'react';

export type OverflowAxis = 'horizontal' | 'vertical';

export function useIsOverflowing<T extends HTMLElement>(
  ref: RefObject<T | null>,
  axis: OverflowAxis,
  contentKey: string,
) {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;

    const measure = () => {
      if (!element) {
        setIsOverflowing(false);
        return;
      }
      setIsOverflowing(
        axis === 'horizontal'
          ? element.scrollWidth > element.clientWidth
          : element.scrollHeight > element.clientHeight,
      );
    };

    measure();

    if (!element) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);
    window.addEventListener('resize', measure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [axis, contentKey, ref]);

  return isOverflowing;
}
